import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { money, percent } from '../../lib/format.js'
import { Card, Empty, Notice, RowsSkeleton, Stat, StatsSkeleton } from '../../components/ui.js'
import { Funnel, LineChart } from '../../components/charts.js'
import { ReminderFunnelCard } from '../../components/ReminderFunnel.js'
import { BellIcon, CartIcon, CheckCircleIcon, RupeeIcon, TargetIcon, TrendUpIcon } from '../../components/icons.js'
import type { AnalyticsPayload } from './types.js'

/**
 * Recovery analytics. Every figure is computed by the API from message rows,
 * provider receipts, link clicks and orders on the same checkout; a figure the
 * data cannot support (a delivery rate with no receipts, a net value with
 * nothing sent) is shown as "—" with the reason, never estimated.
 */

const RANGES = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
]

export function AnalyticsPanel({ shopId, multiStore }: { shopId: string; multiStore: boolean }): ReactNode {
  const [days, setDays] = useState(30)
  const [channel, setChannel] = useState<'all' | 'whatsapp' | 'email'>('all')
  const [campaign, setCampaign] = useState('all')
  const [store, setStore] = useState('')

  const query = useQuery({
    queryKey: ['abandoned-analytics', shopId, days, channel, campaign, store],
    queryFn: () =>
      api<AnalyticsPayload>('/v1/abandoned/analytics', {
        query: { from: new Date(Date.now() - days * 86_400_000).toISOString(), channel, campaign, store: store || undefined },
      }),
  })

  const data = query.data
  const metrics = data?.metrics

  return (
    <div className="stack">
      <div className="toolbar toolbar--card">
        <select className="select" aria-label="Date range" value={days} onChange={(event) => setDays(Number(event.target.value))}>
          {RANGES.map((range) => <option key={range.days} value={range.days}>{range.label}</option>)}
        </select>
        <select className="select" aria-label="Channel" value={channel} onChange={(event) => setChannel(event.target.value as typeof channel)}>
          <option value="all">All channels</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="email">Email</option>
        </select>
        <select className="select" aria-label="Campaign" value={campaign} onChange={(event) => setCampaign(event.target.value)}>
          <option value="all">All sources</option>
          <option value="workflow">Automated workflow</option>
          <option value="manual">Manual reminders</option>
          <option value="legacy">Automatic sweep (earlier)</option>
          {(data?.campaigns ?? []).map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
        </select>
        {multiStore && (
          <select className="select" aria-label="Store" value={store} onChange={(event) => setStore(event.target.value)}>
            <option value="">This store</option>
            <option value="all">All my stores</option>
          </select>
        )}
      </div>

      {query.isPending && <StatsSkeleton count={8} />}
      {query.error && <Card><Empty error title="Could not load analytics">{(query.error as Error).message}</Empty></Card>}

      {data && metrics && (
        <>
          <section className="grid grid--4">
            <Stat label="Abandoned checkouts" value={data.abandoned.total.toLocaleString('en-IN')} icon={<CartIcon size={20} />} tone="var(--bad)" footnote={<span className="faint">in this period</span>} />
            <Stat label="Recoverable value" value={money(data.abandoned.recoverableValue)} icon={<RupeeIcon size={20} />} tone="var(--warn)" footnote={<span className="faint">still open</span>} />
            <Stat label="Messages sent" value={metrics.sent.toLocaleString('en-IN')} icon={<BellIcon size={20} />} tone="var(--brand)" footnote={<span className="faint">{metrics.failed} failed · {metrics.skipped} skipped</span>} />
            <Stat
              label="Delivery rate"
              value={metrics.deliveryRate === null ? '—' : percent(metrics.deliveryRate)}
              icon={<CheckCircleIcon size={20} />}
              tone="var(--info)"
              footnote={<span className="faint">{data.receiptsConfigured ? `${metrics.read} read` : 'needs WhatsApp receipts'}</span>}
            />
            <Stat label="Click rate" value={metrics.clickRate === null ? '—' : percent(metrics.clickRate)} icon={<TargetIcon size={20} />} tone="var(--accent)" footnote={<span className="faint">{metrics.clicked} clicked</span>} />
            <Stat label="Recovered orders" value={metrics.recoveredOrders.toLocaleString('en-IN')} icon={<CheckCircleIcon size={20} />} tone="var(--ok)" footnote={<span className="faint">{money(metrics.recoveredRevenue)} revenue</span>} />
            <Stat label="Recovery rate" value={metrics.recoveryRate === null ? '—' : percent(metrics.recoveryRate)} icon={<TrendUpIcon size={20} />} tone="var(--ok)" footnote={<span className="faint">of {metrics.contacted} contacted</span>} />
            <Stat
              label="Net recovery value"
              value={metrics.netRecovery === null ? '—' : money(metrics.netRecovery)}
              icon={<RupeeIcon size={20} />}
              tone="var(--brand)"
              footnote={<span className="faint">after {money(metrics.messagingCost)} messaging cost</span>}
            />
          </section>

          {!data.receiptsConfigured && (
            <Notice kind="info" title="Delivery and read rates need WhatsApp receipts">
              Messages show as sent when WhatsApp accepts them. Delivered and read appear only once the Wakit status webhook is configured on this deployment.
            </Notice>
          )}

          <div className="grid grid--2">
            <Card title="From message to order">
              {metrics.contacted === 0 ? (
                <Empty title="Nothing sent in this period" />
              ) : (
                <Funnel
                  stages={[
                    { label: 'Messages sent', count: metrics.sent, pct: 100 },
                    ...(data.receiptsConfigured ? [{ label: 'Delivered', count: metrics.delivered, pct: metrics.deliveryRate ?? 0 }] : []),
                    { label: 'Clicked', count: metrics.clicked, pct: metrics.clickRate ?? 0 },
                    { label: 'Recovered orders', count: metrics.recoveredOrders, pct: metrics.sent === 0 ? 0 : Math.round((metrics.recoveredOrders / metrics.sent) * 1000) / 10 },
                  ]}
                />
              )}
            </Card>

            <Card title="By channel" flush>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Channel</th>
                      <th className="right">Sent</th>
                      <th className="right">Recovered</th>
                      <th className="right">Revenue</th>
                      <th className="right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(['whatsapp', 'email'] as const).map((key) => (
                      <tr key={key}>
                        <td>{key === 'whatsapp' ? 'WhatsApp' : 'Email'}</td>
                        <td className="right num">{metrics.byChannel[key].sent}</td>
                        <td className="right num">{metrics.byChannel[key].recovered}</td>
                        <td className="right num">{money(metrics.byChannel[key].revenue)}</td>
                        <td className="right num">{Number(metrics.byChannel[key].cost) > 0 ? money(metrics.byChannel[key].cost) : 'Free'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {metrics.daily.length > 1 && (
            <Card title="Sent and recovered per day">
              <LineChart
                labels={metrics.daily.map((entry) => entry.day.slice(5))}
                series={[
                  { key: 'sent', label: 'Messages sent', color: 'var(--brand)', values: metrics.daily.map((entry) => entry.sent) },
                  { key: 'recovered', label: 'Recovered orders', color: 'var(--ok)', values: metrics.daily.map((entry) => entry.recovered) },
                ]}
                formatValue={(value) => String(Math.round(value))}
              />
            </Card>
          )}

          <p className="muted small">
            How recovery is counted: an order is credited to a reminder only when it was placed on the same checkout, was paid (or confirmed cash on delivery), and came within {data.attributionWindowDays} days after the reminder. Credit goes to the last reminder before the order, so a shopper reminded twice is one recovery.
          </p>
        </>
      )}

      {query.isFetching && !query.isPending && <RowsSkeleton rows={1} />}

      <ReminderFunnelCard scope="shop" shopId={shopId} />
    </div>
  )
}
